import React from 'react';
import {
  Box,
  Flex,
  Button,
  Text,
  useColorMode,
  useColorModeValue,
  IconButton,
  HStack,
  Avatar,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuDivider,
  useBreakpointValue,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ConnectButton from '@/components/common/ConnectButton';
import NetworkStatus from '@/components/common/NetworkStatus';
import PriceDisplay from '@/components/common/PriceDisplay';
import { FiMenu } from 'react-icons/fi';
import Logo from '@/components/common/Logo';

interface HeaderProps {
  onMobileMenuToggle: () => void;
}

const Header: React.FC<HeaderProps> = ({ onMobileMenuToggle }) => {
  const navigate = useNavigate();
  const { t } = useTranslation('common');
  const bg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  // 响应式配置
  const isMobile = useBreakpointValue({ base: true, lg: false });
  const headerPadding = useBreakpointValue({ base: 4, md: 6 });
  const headerHeight = useBreakpointValue({ base: "70px", md: "80px" });

  const handleLogoClick = () => {
    navigate('/welcome');
  };

  return (
    <Box
      as="header"
      bg={bg}
      borderBottom="1px"
      borderColor={borderColor}
      px={headerPadding}
      py={2}
      position="fixed"
      top={0}
      left={0}
      right={0}
      zIndex={1000}
      h={headerHeight}
      boxShadow="sm"
    >
      <Flex justify="space-between" align="center" w="full" h="full">
        {/* 左侧 - Logo、价格组件和移动端菜单按钮 */}
        <HStack spacing={4} flexShrink={0} minW="450px">
          {/* 移动端菜单按钮 */}
          <IconButton
            display={{ base: 'flex', lg: 'none' }}
            aria-label={t('navigation.openMenu')}
            icon={<FiMenu />}
            onClick={onMobileMenuToggle}
            variant="ghost"
            size="sm"
          />
          
          {/* Logo - 可点击跳转到Welcome页面 */}
          <Logo 
            size="xs" 
            showText={!isMobile} 
            onClick={handleLogoClick}
            clickable={true}
          />

          {/* 桌面端价格趋势显示 */}
          <Box 
            display={{ base: 'none', lg: 'block' }} 
            flexShrink={0}
          >
            <PriceDisplay 
              context="navbar"
              showLink={false}
              variant="navbar"
              showWarning={false}
              showChange={true}
            />
          </Box>
        </HStack>

        {/* 中间空白区域 */}
        <Box flex={1} />
        
        {/* 移动端价格显示 */}
        <Box display={{ base: 'block', lg: 'none' }} flexShrink={0}>
          <PriceDisplay 
            context="navbar"
            showLink={false}
            variant="navbar"
            showWarning={false}
            showChange={true}
          />
        </Box>

        {/* 右侧操作区 */}
        <HStack spacing={{ base: 2, md: 3 }} flexShrink={0} minW="200px" justify="flex-end">
          {/* 网络状态 - 移动端隐藏 */}
          <Box display={{ base: 'none', md: 'block' }}>
            <NetworkStatus />
          </Box>

          {/* 钱包连接 */}
          <ConnectButton />
        </HStack>
      </Flex>
    </Box>
  );
};

export default Header; 